import React, { useState, useEffect } from "react";
import StepBreadcrumbs from "../components/StepBreadcrumbsProps";
import { MdSensorWindow } from "react-icons/md";
import { BiSolidFridge, BiSolidCabinet } from "react-icons/bi";
import { subscribeToPrecios } from "../firebase/firebaseService";
import { useTranslation } from "react-i18next";

type Props = {
  setQuoteDetails: React.Dispatch<
    React.SetStateAction<{
      service: string;
      frequency: string;
      location: string;
      bedrooms: number;
      bathrooms: number;
      insideFridge: boolean; // ✅ Agregamos estos valores directamente
      insideOven: boolean;
      insideCabinets: boolean;
      windows: boolean;

      contactDetails: {
        // ✅ Se añade correctamente como en useState
        name: string;
        phone: string;
        address: string;
        email: string;
      };
      price: number;
    }>
  >;
  quoteDetails: {
    service: string;
    frequency: string;
    location: string;
    bedrooms: number;
    bathrooms: number;
    insideFridge: boolean; // ✅ Agregamos estos valores directamente
    insideOven: boolean;
    insideCabinets: boolean;
    windows: boolean;

    contactDetails: {
      // ✅ Se añade contactDetails
      name: string;
      phone: string;
      address: string;
      email: string;
    };
    price: number;
  };
};

const DetailsContact: React.FC<Props> = ({ setQuoteDetails, quoteDetails }) => {
  const [selectedSpecialServices, setSelectedSpecialServices] = useState<
    string[]
  >([]);
  
  const [additionalPrice, setAdditionalPrice] = useState(0);
  const [preciosOrdenados, setPreciosOrdenados] = useState([]);
 
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [lastLocationPrice, setLastLocationPrice] = useState<number>(0);

  const { t } = useTranslation();

  const options =
    preciosOrdenados.length >= 18
      ? [
          {
            name: preciosOrdenados[6].servicio,
            price: preciosOrdenados[6].costo,
          },
          {
            name: preciosOrdenados[7].servicio,
            price: preciosOrdenados[7].costo,
          },
          {
            name: preciosOrdenados[8].servicio,
            price: preciosOrdenados[8].costo,
          },
          {
            name: preciosOrdenados[9].servicio,
            price: preciosOrdenados[9].costo,
          },
          {
            name: preciosOrdenados[10].servicio,
            price: preciosOrdenados[10].costo,
          },
          {
            name: preciosOrdenados[11].servicio,
            price: preciosOrdenados[11].costo,
          },
          {
            name: preciosOrdenados[12].servicio,
            price: preciosOrdenados[12].costo,
          },
        ]
      : [];
  
    // Cargar datos desde Firebase
  useEffect(() => {
    const unsubscribe = subscribeToPrecios((data) => {
      const sortedPrices = [...data].sort((a, b) => a.id - b.id);
      setPreciosOrdenados(sortedPrices);
    });

    return () => unsubscribe();
  }, []);

  // Definir los servicios especiales con los precios actualizados
  const specialServices =
    preciosOrdenados.length >= 18
      ? [
        
          {
            name: preciosOrdenados[3].servicio,
            price: preciosOrdenados[3].costo,
            icon: <BiSolidFridge className="w-[30px] h-[30px]"/>,
          },
          {
            name: preciosOrdenados[4].servicio,
            price: preciosOrdenados[4].costo,
            icon: <BiSolidCabinet className="w-[30px] h-[30px]"/>,
          },
          {
            name: preciosOrdenados[5].servicio,
            price: preciosOrdenados[5].costo,
            icon: <MdSensorWindow className="w-[30px] h-[30px]"/>,
          },
        ]
      : [];
   

  const handleSpecialServiceChange = (serviceName: string, price: number) => {
    if (specialServices.length === 0) return;

    const isSelected = selectedSpecialServices.includes(serviceName);
    let updatedServices: string[];
    let updatedPrice: number;

    if (isSelected) {
      updatedServices = selectedSpecialServices.filter(
        (service) => service !== serviceName
      );
      updatedPrice = additionalPrice - price;
    } else {
      updatedServices = [...selectedSpecialServices, serviceName];
      updatedPrice = additionalPrice + price;
    }

    setSelectedSpecialServices(updatedServices);
    setAdditionalPrice(updatedPrice);

    // Convertir los servicios seleccionados a propiedades booleanas
    setQuoteDetails((prev) => ({
      ...prev,
      specialServices: updatedServices,
      price: prev.price + (isSelected ? -price : price),
      insideOven: updatedServices.includes("Inside oven") ? true : false,
      insideFridge: updatedServices.includes("Inside fridge") ? true : false,
      insideCabinets: updatedServices.includes("Inside cabinets")
        ? true
        : false,
      windows: updatedServices.includes("Windows (interior)") ? true : false,
    }));
  };

  const handleContactChange = (
    field: "name" | "phone" | "address" | "email",
    value: string
  ) => {
    setQuoteDetails((prev) => ({
      ...prev,
      contactDetails: {
        ...prev.contactDetails,
        [field]: value, // ✅ Se actualiza dinámicamente cualquier campo
      },
    }));
  };

  const handleLocationChange = (serviceName: string, price: number) => {
    if (selectedLocation === serviceName) return;

    // Restar el precio del servicio anterior antes de actualizar
    setAdditionalPrice((prevPrice) => prevPrice - lastLocationPrice + price);
    setQuoteDetails((prev) => ({
      ...prev,
      price: prev.price - lastLocationPrice + price,
      location: serviceName, // Agregamos la ubicación seleccionada al quoteDetails
    }));

    // Guardar la nueva ubicación seleccionada y su precio
    setSelectedLocation(serviceName);
    setLastLocationPrice(price);
  };

  return (
    <div className="p-6 w-[100%] ">
      <div>
        <StepBreadcrumbs currentStep={2} />
      </div>
      <hr className="border-t-2 mb-6"></hr>
      <h2 className="text-[12px] text-textTitle font-bold mb-2">
        {t("detailquote.title")}
      </h2>

      <div className="grid grid-cols-3 gap-6">
        {specialServices.length > 0 ? (
          specialServices.map((service) => (
            <div
              key={service.name}
              onClick={() =>
                handleSpecialServiceChange(service.name, service.price)
              }
              className={`border p-4 py-8 rounded-lg flex flex-col items-center cursor-pointer transition duration-300 
                ${
                  selectedSpecialServices.includes(service.name)
                    ? "border-bgPrimary border-[3px] text-textPrimary font-semibold bg-blue-100"
                    : "bg-white border-gray-300 border-2 text-textTipic hover:bg-blue-100 font-semibold"
                }`}
            >
              <div className="text-2xl mb-2">{service.icon}</div>
              <span className="font-semibold">
                {t(
                  `detailquote.${service.name
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`
                )}
              </span>
            </div>
          ))
        ) : (
          <p>Loading services...</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 w-full">
        <div>
          <h2 className="text-[12px] text-textTitle font-bold mb-2 mt-12">
            {t("detailquote.contact")}
          </h2>
          <div className="self-start">
            <input
              type="text"
              placeholder={t("detailquote.name")}
              value={quoteDetails.contactDetails.name}
              onChange={(e) => handleContactChange("name", e.target.value)}
              className="border rounded-lg p-4 w-full text-textTipic focus:outline-none focus:border-bgPrimary font-medium mb-6"
            />
            <input
              type="text"
              placeholder={t("detailquote.phone")}
              value={quoteDetails.contactDetails.phone}
              onChange={(e) => handleContactChange("phone", e.target.value)}
              className="border rounded-lg p-4 w-full text-textTipic focus:outline-none focus:border-bgPrimary font-medium mb-6"
            />
            <input
              type="text"
              placeholder={t("detailquote.email")}
              value={quoteDetails.contactDetails.email}
              onChange={(e) => handleContactChange("email", e.target.value)}
              className="border rounded-lg p-4 w-full text-textTipic focus:outline-none focus:border-bgPrimary font-medium mb-6"
            />
          </div>
        </div>

        <div className="flex flex-col items-start gap-2 self-start  w-full">
        <h2 className="text-[12px] text-textTitle font-bold mt-12">
            {t("detailquote.location")}
          </h2>
        
          <select
            id="select"
            className="p-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            value={selectedLocation || ""}
            onChange={(e) => {
              const selectedOption = options.find(
                (opt) => opt.name === e.target.value
              );
              if (selectedOption) {
                handleLocationChange(selectedOption.name, selectedOption.price);
              }
            }}
          >
            <option value="" disabled>
            {t("detailquote.selection")}
            </option>
            {options.map((option, index) => (
              <option key={index} value={option.name}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default DetailsContact;
